// Read-only feed of platform_admin_audit_log via the list_platform_admin_audit_log
// RPC (see migration 0103) -- every module toggle, tenant/admin creation,
// tenant suspend/reactivate, pricing structure change, and superadmin
// grant/revoke lands here, tagged with who and when. Continuous field
// edits (typing a tier's dollar amount) aren't logged individually -- only
// the discrete add/delete/structural actions -- so this stays a readable
// history instead of a keystroke log.
import React, { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/use-toast';
import { History, RefreshCw } from 'lucide-react';

interface AuditLogRow {
  id: number;
  actor_email: string | null;
  action: string;
  target_type: string;
  target_id: string | null;
  detail: Record<string, unknown> | null;
  created_at: string;
}

const ACTION_LABELS: Record<string, string> = {
  module_toggled: 'toggled a module',
  tenant_active_changed: 'changed tenant status',
  tenant_created: 'created a tenant',
  admin_created: 'created an admin login',
  pricing_basis_changed: 'changed a billing basis',
  pricing_tier_added: 'added a rate tier',
  pricing_tier_deleted: 'removed a rate tier',
  pricing_discount_tier_added: 'added a discount tier',
  pricing_discount_tier_deleted: 'removed a discount tier',
  superadmin_invited: 'invited a superadmin',
  superadmin_active_changed: 'changed a superadmin\'s access',
};

function describeDetail(row: AuditLogRow): string {
  const d = row.detail || {};
  switch (row.action) {
    case 'module_toggled':
      return `${d.module_key} ${d.is_enabled ? 'on' : 'off'} for ${d.tenant_name ?? row.target_id}`;
    case 'tenant_active_changed':
      return `${d.tenant_name ?? row.target_id} -> ${d.is_active ? 'Active' : 'Inactive'}`;
    case 'tenant_created':
      return `${d.tenant_name ?? row.target_id}`;
    case 'admin_created':
      return `${d.driver_name} at ${d.tenant_name ?? row.target_id}`;
    case 'pricing_basis_changed':
      return `${row.target_id} -> ${d.basis}`;
    case 'pricing_tier_added':
      return `${d.module_key ?? row.target_id}`;
    case 'pricing_tier_deleted':
      return `${d.module_key ?? row.target_id} (${d.tier_min}-${d.tier_max ?? 'unlimited'} @ $${d.unit_price})`;
    case 'pricing_discount_tier_added':
      return 'new discount tier';
    case 'pricing_discount_tier_deleted':
      return `${d.min_modules}-${d.max_modules ?? 'unlimited'} modules @ ${d.discount_percent}%`;
    case 'superadmin_invited':
      return `${d.email}`;
    case 'superadmin_active_changed':
      return `${d.email} -> ${d.is_active ? 'Active' : 'Inactive'}`;
    default:
      return JSON.stringify(d);
  }
}

const SuperadminAuditLog: React.FC = () => {
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    const { data, error } = await supabase.rpc('list_platform_admin_audit_log', { p_limit: 200 });
    if (error) {
      toast({ title: 'Error', description: 'Failed to load audit log: ' + error.message, variant: 'destructive' });
      setLoading(false);
      return;
    }
    setRows((data as AuditLogRow[]) || []);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  return (
    <div className="max-w-3xl">
      <Card className="shadow-lg bg-card border border-border">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between gap-2">
            <CardTitle className="text-base text-foreground flex items-center gap-2">
              <History className="h-4 w-4 text-yellow-500" />
              Audit Log
            </CardTitle>
            <Button size="sm" variant="outline" onClick={load} disabled={loading}>
              <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mt-1">Most recent 200 platform-level actions, newest first.</p>
        </CardHeader>
        <CardContent className="space-y-1.5">
          {loading ? (
            <p className="text-sm text-muted-foreground">Loading...</p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">No actions logged yet.</p>
          ) : (
            rows.map((row) => (
              <div key={row.id} className="flex items-start justify-between gap-3 p-2.5 rounded-lg border border-border bg-secondary/20 text-sm">
                <div className="min-w-0">
                  <p className="text-foreground">
                    <span className="font-medium">{row.actor_email ?? 'Unknown'}</span>{' '}
                    <span className="text-muted-foreground">{ACTION_LABELS[row.action] ?? row.action}</span>
                  </p>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{describeDetail(row)}</p>
                </div>
                <span className="text-xs text-muted-foreground shrink-0 tabular-nums">
                  {new Date(row.created_at).toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' })}
                </span>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuperadminAuditLog;
